// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { For, Show, createSignal } from "solid-js";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import type { GroupBody, GroupProjectBody, MemberBody } from "./api/generated";
import {
  assignMemberGroup,
  createGroup,
  deleteGroup,
  listGroupProjects,
  listGroups,
  listMembers,
  updateGroup,
} from "./api/generated";
import { apiErrorMessage, authSlotHeaders } from "./api/runtime";
import { Button, Field, Form, ValidationMessage } from "./components/forms";
import { Heading } from "./components/layout";
import { AccessNotPermitted, useEntitlement } from "./entitlements";
import {
  groupCreateFormSchema,
  groupUpdateFormSchema,
} from "./form-validation";
import { AppLink, useGo } from "./navigation";

type Props = { slot: number; account: string; rest: string[] };
const roleName = (role: string) => (role === "admin" ? "Admin" : "Member");
const clusterRoleNames: Record<GroupProjectBody["clusterRole"], string> = {
  admins: "Admin",
  managers: "Manager",
  operators: "Operator",
  editors: "Editor",
  viewers: "Viewer",
};

function RoleField(p: { value?: string }) {
  return (
    <div class="field">
      <label for="group-role">Account role</label>
      <select
        class="select select-bordered w-full"
        id="group-role"
        name="role"
        value={p.value ?? "member"}
        required
      >
        <option value="member">Member</option>
        <option value="admin">Admin</option>
      </select>
      <ValidationMessage name="role" />
    </div>
  );
}

function GroupMembers(p: Props & { group: GroupBody }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = createSignal("");
  const [error, setError] = createSignal("");
  const members = createQuery(() => ({
    queryKey: [p.slot, p.account, "members", "group-edit"],
    queryFn: async () =>
      (
        await listMembers({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account },
          headers: authSlotHeaders(p.slot),
        })
      ).data.items ?? [],
  }));
  const assign = async (member: MemberBody) => {
    setBusy(member.id);
    setError("");
    try {
      await assignMemberGroup({
        throwOnError: true,
        responseStyle: "fields",
        path: { account: p.account, member: member.id },
        headers: authSlotHeaders(p.slot),
        body: { groupId: p.group.id },
      });
      await queryClient.invalidateQueries({
        queryKey: [p.slot, p.account, "members"],
      });
    } catch (reason) {
      setError(apiErrorMessage(reason));
    } finally {
      setBusy("");
    }
  };
  return (
    <section class="mt-8 grid min-w-0 gap-4">
      <div>
        <h2 class="text-xl font-bold">Account members</h2>
        <p class="mt-1 text-sm text-slate-500">
          Assign members to this group. Assigning a member moves them from their
          current group.
        </p>
      </div>
      <Show when={members.isPending}>
        <p>Loading members…</p>
      </Show>
      <Show when={members.isError}>
        <div role="alert" class="alert alert-error">
          Unable to load members.
        </div>
      </Show>
      <Show when={members.isSuccess}>
        <div class="grid min-w-0 gap-3">
          <For each={members.data}>
            {(member) => (
              <article class="panel flex min-w-0 flex-col gap-3 p-4 sm:flex-row sm:items-center">
                <div class="min-w-0 flex-1">
                  <h3 class="truncate font-semibold">{member.email}</h3>
                  <p class="truncate text-sm text-slate-500">
                    Current group: {member.groupName}
                  </p>
                </div>
                <button
                  class={`btn shrink-0 ${member.groupId === p.group.id ? "btn-inactive" : "btn-secondary"}`}
                  type="button"
                  disabled={
                    member.groupId === p.group.id || busy() === member.id
                  }
                  onClick={() => assign(member)}
                >
                  {member.groupId === p.group.id
                    ? "Assigned"
                    : busy() === member.id
                      ? "Assigning…"
                      : "Assign"}
                </button>
              </article>
            )}
          </For>
        </div>
      </Show>
      <Show when={error()}>
        <div role="alert" class="alert alert-error">
          {error()}
        </div>
      </Show>
    </section>
  );
}

function GroupProjects(p: Props & { group: GroupBody }) {
  const projects = createQuery(() => ({
    queryKey: [p.slot, p.account, "groups", p.group.id, "projects"],
    refetchOnMount: "always",
    queryFn: async () =>
      (
        await listGroupProjects({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account, id: p.group.id },
          headers: authSlotHeaders(p.slot),
        })
      ).data.items ?? [],
  }));
  return (
    <section class="mt-8 grid min-w-0 gap-4" aria-labelledby="group-projects-heading">
      <div>
        <h2 id="group-projects-heading" class="text-xl font-bold">
          Projects
        </h2>
        <p class="mt-1 text-sm text-slate-500">
          Projects this group can access and its cluster role in each project.
        </p>
      </div>
      <Show when={projects.isPending}>
        <p>Loading projects…</p>
      </Show>
      <Show when={projects.isError}>
        <div role="alert" class="alert alert-error">
          Unable to load projects.
        </div>
      </Show>
      <Show when={projects.isSuccess && projects.data.length === 0}>
        <p class="text-sm text-slate-500">
          This group is not assigned to any projects.
        </p>
      </Show>
      <Show when={projects.isSuccess && projects.data.length > 0}>
        <div class="grid min-w-0 gap-3">
          <For each={projects.data}>
            {(project) => (
              <article class="panel flex min-w-0 items-center gap-3 p-4">
                <div class="min-w-0 flex-1">
                  <AppLink
                    class="block truncate font-semibold text-primary hover:underline"
                    to={`/u${p.slot}/${p.account}/projects/${project.id}/edit`}
                  >
                    {project.name}
                  </AppLink>
                  <p class="text-sm text-slate-500">
                    {clusterRoleNames[project.clusterRole]}
                  </p>
                </div>
              </article>
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}

export function GroupsPage(p: Props) {
  const go = useGo();
  const queryClient = useQueryClient();
  const canManage = useEntitlement("account.group.manage");
  const [error, setError] = createSignal("");
  const groups = createQuery(() => ({
    queryKey: [p.slot, p.account, "groups"],
    queryFn: async () =>
      (
        await listGroups({
          throwOnError: true,
          responseStyle: "fields",
          path: { account: p.account },
          headers: authSlotHeaders(p.slot),
        })
      ).data.items ?? [],
  }));
  const base = `/u${p.slot}/${p.account}/groups`;
  const current = () => groups.data?.find((group) => group.id === p.rest[1]);
  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: [p.slot, p.account, "groups"],
      exact: true,
    });
  if (p.rest[1] && !canManage())
    return (
      <>
        <Heading title={p.rest[1] === "new" ? "Add group" : "Edit group"} />
        <AccessNotPermitted />
      </>
    );
  if (p.rest[1] === "new")
    return (
      <>
        <Heading title="Add group" />
        <section class="panel max-w-2xl p-6">
          <Form
            schema={groupCreateFormSchema}
            submitLabel="Create group"
            action={async (data) => {
              await createGroup({
                throwOnError: true,
                responseStyle: "fields",
                path: { account: p.account },
                headers: authSlotHeaders(p.slot),
                body: {
                  name: data.name ?? "",
                  role: data.role as GroupBody["role"],
                },
              });
              await refresh();
              go(base);
            }}
          >
            <Field
              name="name"
              label="Group name"
              required
              minLength={1}
              maxLength={120}
            />
            <RoleField />
          </Form>
        </section>
      </>
    );
  if (p.rest[1])
    return (
      <>
        <Heading title="Edit group" />
        <Show when={groups.isPending}>
          <p>Loading group…</p>
        </Show>
        <Show when={groups.isError}>
          <div role="alert" class="alert alert-error">
            Unable to load group.
          </div>
        </Show>
        <Show when={groups.isSuccess && !current()}>
          <div role="alert" class="alert alert-error">
            Group not found.
          </div>
        </Show>
        <Show when={current()} keyed>
          {(group) => (
            <>
              <section class="panel max-w-2xl p-6">
                <Form
                  schema={groupUpdateFormSchema}
                  submitLabel="Save group"
                  successMessage="Group updated."
                  action={async (data) => {
                    await updateGroup({
                      throwOnError: true,
                      responseStyle: "fields",
                      path: { account: p.account, id: group.id },
                      headers: authSlotHeaders(p.slot),
                      body: {
                        name: data.name ?? "",
                        role: data.role as GroupBody["role"],
                      },
                    });
                    await queryClient.invalidateQueries({
                      queryKey: [p.slot, p.account, "groups"],
                      exact: true,
                      refetchType: "none",
                    });
                  }}
                >
                  <Field
                    name="name"
                    label="Group name"
                    value={group.name}
                    required
                    minLength={1}
                    maxLength={120}
                  />
                  <RoleField value={group.role} />
                </Form>
              </section>
              <GroupProjects {...p} group={group} />
              <GroupMembers {...p} group={group} />
            </>
          )}
        </Show>
      </>
    );
  const remove = async (group: GroupBody) => {
    if (!confirm(`Delete group “${group.name}”?`)) return;
    setError("");
    try {
      await deleteGroup({
        throwOnError: true,
        responseStyle: "fields",
        path: { account: p.account, id: group.id },
        headers: authSlotHeaders(p.slot),
      });
      await refresh();
    } catch (reason) {
      setError(apiErrorMessage(reason));
    }
  };
  return (
    <>
      <Heading
        title="Groups"
        copy="Manage groups, account roles, and member assignments."
        action={
          <Show when={canManage()}>
            <Button onClick={() => go(`${base}/new`)}>Add new</Button>
          </Show>
        }
      />
      <Show when={error()}>
        <div role="alert" class="alert alert-error mb-4">
          {error()}
        </div>
      </Show>
      <Show when={groups.isPending}>
        <p>Loading groups…</p>
      </Show>
      <Show when={groups.isError}>
        <div role="alert" class="alert alert-error">
          Unable to load groups.
        </div>
      </Show>
      <Show when={groups.isSuccess}>
        <div class="grid gap-3">
          <For each={groups.data}>
            {(group) => (
              <article class="panel flex min-w-0 flex-col gap-3 p-4 sm:flex-row sm:items-center">
                <div class="min-w-0 flex-1">
                  <h2 class="truncate font-semibold">{group.name}</h2>
                  <p class="text-sm text-slate-500">{roleName(group.role)}</p>
                </div>
                <Show when={canManage()}>
                  <div class="flex flex-wrap gap-2">
                    <AppLink
                      class="btn btn-ghost text-base"
                      to={`${base}/${group.id}/edit`}
                    >
                      Edit
                    </AppLink>
                    <button
                      class="btn btn-ghost text-base text-error"
                      type="button"
                      onClick={() => remove(group)}
                    >
                      Delete
                    </button>
                  </div>
                </Show>
              </article>
            )}
          </For>
        </div>
      </Show>
    </>
  );
}
